import { decodeVisualGroups, type VisualGrouping } from "../visualGroups";
import type { Question, QuestionGroupChild } from "../../util/types";
import type { QuestionCore } from "../contracts";
import { questionGroupContainerValidationError } from "../questionGroup/core";

export type GridQuestionGroupRow = {
  value: string;
  text: string;
  display_text: boolean;
};

export type GridQuestionGroupRaw = Question["question"] & {
  type: "grid_question_group";
  content: string;
  group_id: string;
  active_child_id: string;
  row_name: string;
  rows: GridQuestionGroupRow[];
  children: QuestionGroupChild[];
};

export type GridQuestionGroupDefinition = VisualGrouping & {
  heading: string;
  groupId: string;
  activeChildId: string;
  rowName: string;
  rows: readonly GridQuestionGroupRow[];
  fields: readonly number[];
  children: readonly QuestionGroupChild[];
};

export type GridQuestionGroupDraft = Record<string, never>;
export type GridQuestionGroupEvent = never;
export type GridQuestionGroupResponse = {
  type: "grid_question_group";
  value: string;
};

const supportedChildTypes = new Set([
  "text",
  "numeric",
  "select",
  "multi-select",
  "image",
]);

export const isGridQuestionGroupRaw = (
  raw: Question["question"],
): raw is GridQuestionGroupRaw => raw.type === "grid_question_group";

export const gridQuestionGroupValidationError = (
  raw: Question["question"],
): string | null => {
  if (!isGridQuestionGroupRaw(raw)) {
    return "Question is not a grid question group.";
  }
  const groupError = questionGroupContainerValidationError(raw);
  if (groupError) return groupError;
  if (typeof raw.row_name !== "string" || !raw.row_name) {
    return "Grid question group requires a row name.";
  }
  if (!Array.isArray(raw.rows) || raw.rows.length === 0) {
    return "Grid question group requires at least one row.";
  }
  const rowValues = raw.rows.map((row) => row?.value);
  if (
    raw.rows.some(
      (row) =>
        !row ||
        typeof row.value !== "string" ||
        !row.value ||
        typeof row.text !== "string" ||
        typeof row.display_text !== "boolean",
    ) ||
    new Set(rowValues).size !== rowValues.length
  ) {
    return "Grid question group rows must have unique string values.";
  }
  if (
    raw.children.some(
      (child) =>
        typeof child.row !== "string" ||
        !rowValues.includes(child.row) ||
        !Number.isInteger(child.field) ||
        child.field < 0 ||
        !supportedChildTypes.has(child.question.type),
    )
  ) {
    return "Grid question group contains an unsupported or misplaced child.";
  }
  const placements = raw.children.map(
    (child) => `${child.row}\u0000${child.field}`,
  );
  if (new Set(placements).size !== placements.length) {
    return "Grid question group child placements must be unique.";
  }
  return null;
};

export const hasValidGridQuestionGroupMetadata = (
  raw: Question["question"],
): raw is GridQuestionGroupRaw =>
  gridQuestionGroupValidationError(raw) === null;

export const gridQuestionGroupCore: QuestionCore<
  GridQuestionGroupRaw,
  GridQuestionGroupDefinition,
  GridQuestionGroupDraft,
  GridQuestionGroupEvent,
  GridQuestionGroupResponse
> = {
  decode: (raw) => {
    const validationError = gridQuestionGroupValidationError(raw);
    if (validationError) throw new Error(validationError);
    return {
      ...decodeVisualGroups(raw),
      heading: raw.content,
      groupId: raw.group_id,
      activeChildId: raw.active_child_id,
      rowName: raw.row_name,
      rows: raw.rows,
      fields: [...new Set(raw.children.map((child) => child.field))].sort(
        (left, right) => left - right,
      ),
      children: raw.children,
    };
  },
  initialize: () => ({}),
  update: (_definition, draft) => draft,
  validate: () => ({ valid: true }),
  encode: () => {
    throw new Error("Grid question groups submit through their child modules.");
  },
  projectTranscript: (definition) => ({
    kind: "question_group",
    question: definition.heading,
    answer: "",
  }),
};
